import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronRight, Zap, Brain, Calendar, Target, Network, Rocket } from "lucide-react";

interface Phase {
  number: number;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  solving: string;
  mastery: Array<{
    title: string;
    description: string;
  }>;
  outcome: string;
  future?: boolean;
}

const phases: Phase[] = [
  {
    number: 1,
    title: "Stop Doing Manual Work",
    subtitle: "Get back 5-10 hours per week. No coding required.",
    icon: Zap,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    solving: "You're spending 5-10 hours every week copying data from Google Ads, building reports, and hunting for problems. You want that time back and you want issues to come to you.",
    mastery: [
      {
        title: "Dashboard Tools",
        description: "Start with built-in Google Ads dashboards, Supermetrics (expensive), or Swydo to automate basic reporting without any coding."
      },
      {
        title: "Google Ads Scripts (Copy/Paste)",
        description: "Use pre-built scripts to pull data and send notifications. No JavaScript knowledge required - just copy, paste, configure."
      },
      {
        title: "Management by Exception",
        description: "Set up alerts that bring errors, anomalies, and outliers to you via email, Slack, or text. Stop hunting for problems."
      },
      {
        title: "Low-Code Automation",
        description: "Graduate to tools like Zapier, Make.com, or n8n to connect systems and automate workflows without coding."
      },
      {
        title: "Apps Script (Optional)",
        description: "If you want more control, Google Ads Scripts becomes your gateway to learning Apps Script for programmatic spreadsheets."
      },
      {
        title: "Rule-Based Systems",
        description: "Everything is deterministic at this stage. \"If this happens, do that.\" Simple, reliable, automated."
      }
    ],
    outcome: "Reports and alerts that run automatically. Problems come to you instead of you going to find them. Get back 5-10 hours per week without writing a single line of code (unless you want to)."
  },
  {
    number: 2,
    title: "Add AI Analysis",
    subtitle: "AI helps you understand what the data means.",
    icon: Brain,
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    solving: "You have automated data, but you're still manually analyzing what it means. You spend time figuring out \"why did CPA increase?\" and writing explanations for clients. You want AI to help with analysis.",
    mastery: [
      {
        title: "Manual AI Analysis",
        description: "Download CSVs from Google Ads, paste them into ChatGPT with saved prompts. Cut and paste the analysis. Simple, but effective."
      },
      {
        title: "=AI Functions in Sheets",
        description: "Use AI formulas directly in Google Sheets to analyze data without leaving your spreadsheet."
      },
      {
        title: "Better Visualization",
        description: "Graduate from basic dashboards to Looker Studio for dynamic, interactive reporting. Create PDF docs for clients."
      },
      {
        title: "Prompt Development",
        description: "Learn what questions to ask AI and how to ask them consistently to get useful analysis instead of generic observations."
      },
      {
        title: "Custom Displays (Optional)",
        description: "If standard dashboards don't show what you need, start building custom displays with code. Tools like 8020agent show what's possible."
      },
      {
        title: "Combined Workflow",
        description: "Automated data collection from Phase 1, plus AI analysis, plus better dashboards. The data flows, AI helps you understand it."
      }
    ],
    outcome: "Data that explains itself. Instead of staring at numbers trying to spot patterns, AI analyzes changes and tells you what matters. You still review and refine, but the heavy lifting is done."
  },
  {
    number: 3,
    title: "Automate the Insights",
    subtitle: "AI analysis runs on schedule. You review, don't write.",
    icon: Calendar,
    color: "text-green-600",
    bgColor: "bg-green-50",
    solving: "AI helps with analysis, but you're still manually triggering it - downloading data, pasting into ChatGPT, copying results back. You want AI analysis to run automatically on a schedule.",
    mastery: [
      {
        title: "LLM API Integration",
        description: "Connect Claude or GPT APIs to your Google Ads Scripts or Apps Scripts so AI analysis runs automatically."
      },
      {
        title: "Automated Analysis Workflows",
        description: "Set up scheduled processes that pull data, run AI analysis, and deliver insights without you touching anything."
      },
      {
        title: "Prompt Engineering",
        description: "Refine your prompts to get consistent, actionable insights every time, not generic observations."
      },
      {
        title: "Structured Outputs",
        description: "Get AI to return data in formats you can use programmatically (JSON, formatted tables) instead of just paragraphs of text."
      },
      {
        title: "Custom Applications (Optional)",
        description: "Build your own apps that combine data collection, AI analysis, and custom visualization. 8020agent is an example of this approach."
      },
      {
        title: "Cost Management",
        description: "Learn to work within token limits and API costs so automated analysis doesn't blow your budget."
      }
    ],
    outcome: "Insights that generate automatically. Every Monday morning, AI has already analyzed last week's performance and identified what matters. You review and act, but you never write analysis again."
  },
  {
    number: 4,
    title: "Add Business Context",
    subtitle: "AI gives personalized advice based on your specific situation.",
    icon: Target,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
    solving: "AI analyzes data automatically, but it doesn't understand YOUR business. It doesn't know client priorities, seasonal patterns, what worked last time, or how this client differs from others. You want AI advice that's personalized, not generic.",
    mastery: [
      {
        title: "Knowledge Base Building",
        description: "Document client goals, industry patterns, successful strategies, and historical decisions. Make your business knowledge searchable."
      },
      {
        title: "Memory Systems",
        description: "Use Claude Projects or use simple markdown files to give AI persistent context about your clients and business."
      },
      {
        title: "Pattern Recognition",
        description: "Track what works across accounts. Document seasonal variations, successful responses to common situations, and early warning signs."
      },
      {
        title: "Context Integration",
        description: "Feed business context to AI so recommendations consider client-specific goals, constraints, and history, not just current metrics."
      },
      {
        title: "Advanced Options",
        description: "If building custom systems, explore RAG (vector databases) for searching large knowledge bases, or predictive models for forecasting."
      }
    ],
    outcome: "AI that gives advice specific to your situation. Ask \"Should I scale this campaign?\" and get answers that consider this client's goals, past performance, seasonal patterns, and what's worked in similar situations. Generic insights become strategic recommendations."
  },
  {
    number: 5,
    title: "Build Your Business Brain",
    subtitle: "Work ON business, not IN it. Foundation for autonomous execution.",
    icon: Network,
    color: "text-red-600",
    bgColor: "bg-red-50",
    solving: "You have powerful AI systems, but they're still separate - one for analysis, one for execution, one for client communication. You want everything integrated into one unified \"brain\" that runs your entire operation and provides the foundation for safe autonomous execution.",
    mastery: [
      {
        title: "Unified Knowledge Base",
        description: "Centralize all data, client information, historical decisions, successful strategies, and business processes in one searchable system accessible to all AI operations."
      },
      {
        title: "Cross-System Orchestration",
        description: "Coordinate actions across platforms (Google Ads, Facebook, Analytics, CRM, email). AI manages dependencies, resolves conflicts, optimizes for business goals."
      },
      {
        title: "Decision Frameworks",
        description: "Codify how you make decisions so AI balances multiple objectives (growth vs. efficiency, risk vs. opportunity) the way you would."
      },
      {
        title: "Business Process Automation",
        description: "Automate entire workflows end-to-end: client onboarding, monthly reviews, performance reporting, team coordination, client communication."
      },
      {
        title: "Learning and Adaptation",
        description: "System tracks what works, updates strategies based on results, identifies patterns across all accounts, and continuously improves itself."
      },
      {
        title: "Strategic Intelligence",
        description: "AI analyzes your business itself to identify growth opportunities, optimize resource allocation, and inform strategic planning."
      }
    ],
    outcome: "A complete business operating system. AI manages accounts, communicates with clients, coordinates your team, handles operations, learns from every outcome, and improves continuously. You work ON the business (strategy, relationships, growth), not IN it (daily operations). This is the 8020brain - and the foundation you need before letting AI execute changes autonomously."
  },
  {
    number: 6,
    title: "Let AI Execute",
    subtitle: "AI takes actions within guardrails. You oversee, don't operate.",
    icon: Rocket,
    color: "text-indigo-600",
    bgColor: "bg-indigo-50",
    future: true,
    solving: "AI tells you what to do, but you still have to log in, make changes, and monitor results. You want AI to actually take actions, not just make recommendations. Once we have enough TRUST in AI (built through Phase 5's business brain), this becomes possible.",
    mastery: [
      {
        title: "AI with Tools",
        description: "Give AI the ability to take actions through MCP (Model Context Protocol), Google Ads API, or other integration tools."
      },
      {
        title: "Google Ads API",
        description: "Enable programmatic changes to campaigns, budgets, bids, and ads. AI can modify accounts directly instead of just suggesting changes."
      },
      {
        title: "Safety and Guardrails",
        description: "Set spend limits, define what AI can change autonomously vs. what requires approval, and log everything for accountability. Your business brain (Phase 5) provides the decision framework."
      },
      {
        title: "Autonomous Operations",
        description: "Configure AI to handle routine optimizations within your defined boundaries - pausing underperforming elements, shifting budgets, making bid adjustments."
      },
      {
        title: "Monitoring and Oversight",
        description: "Build systems that alert you when AI makes changes, track performance of automated decisions, and provide override controls."
      },
      {
        title: "Multi-Step Workflows",
        description: "Let AI execute complex sequences - analyze data, determine action, make change, monitor result, adjust if needed."
      }
    ],
    outcome: "AI that works while you sleep. It monitors accounts continuously, makes routine optimizations within your guardrails, and alerts you when human judgment is needed. You approve strategic moves, but daily operations run themselves. You've shifted from operator to overseer."
  }
];

const PhaseCard: React.FC<{ phase: Phase; isExpanded: boolean; onToggle: () => void }> = ({
  phase,
  isExpanded,
  onToggle,
}) => {
  const Icon = phase.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mb-6"
    >
      <div
        onClick={onToggle}
        className={`${phase.bgColor} rounded-lg p-6 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 ${isExpanded ? 'border-gray-300' : 'border-transparent'}`}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4 flex-1">
            <div className={`${phase.color} p-3 bg-white rounded-lg shadow-sm`}>
              <Icon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className={`text-2xl font-bold ${phase.color}`}>
                  Phase {phase.number}
                </h3>
                {phase.future && (
                  <span className="text-xs bg-indigo-600 text-white px-2 py-1 rounded-full">
                    Future
                  </span>
                )}
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-2">
                {phase.title}
              </h4>
              <p className="text-gray-600 italic">
                {phase.subtitle}
              </p>
            </div>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <ChevronDown className="w-6 h-6 text-gray-400" />
          </motion.div>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="mt-6 pt-6 border-t border-gray-200">
                {/* What You're Solving */}
                <div className="mb-6">
                  <h5 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${phase.color.replace('text-', 'bg-')}`}></span>
                    What You're Solving
                  </h5>
                  <p className="text-gray-700 ml-4">{phase.solving}</p>
                </div>

                {/* What You'll Master */}
                <div className="mb-6">
                  <h5 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${phase.color.replace('text-', 'bg-')}`}></span>
                    What You'll Master
                  </h5>
                  <div className="ml-4 space-y-3">
                    {phase.mastery.map((item, idx) => (
                      <div key={idx} className="flex gap-2">
                        <ChevronRight className={`w-5 h-5 ${phase.color} flex-shrink-0 mt-0.5`} />
                        <div>
                          <span className="font-semibold text-gray-900">{item.title}</span>
                          <span className="text-gray-600"> - {item.description}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What You Get */}
                <div className="bg-white rounded-lg p-4 border-l-4" style={{ borderColor: phase.color.replace('text-', '#') }}>
                  <h5 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${phase.color.replace('text-', 'bg-')}`}></span>
                    What You Get
                  </h5>
                  <p className="text-gray-700 ml-4 font-medium">{phase.outcome}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default function ProgressionsPage() {
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);
  const [expandAll, setExpandAll] = useState(false);

  const handleToggle = (phaseNumber: number) => {
    setExpandedPhase(expandedPhase === phaseNumber ? null : phaseNumber);
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedPhase(null);
      setExpandAll(false);
    } else {
      setExpandAll(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            From Ads to AI
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Your roadmap from manual Google Ads management to running an AI-powered business.
          </p>
          <div className="mt-6">
            <button
              onClick={handleExpandAll}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium underline"
            >
              {expandAll ? "Collapse All" : "Expand All"}
            </button>
          </div>
        </motion.div>

        {/* Progress Indicator */}
        <div className="mb-12 flex items-center justify-center gap-2">
          {phases.map((phase, idx) => (
            <React.Fragment key={phase.number}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: idx * 0.1, duration: 0.3 }}
                className={`w-3 h-3 rounded-full ${
                  expandedPhase === phase.number || expandAll
                    ? phase.color.replace('text-', 'bg-')
                    : 'bg-gray-300'
                } transition-colors duration-300`}
              />
              {idx < phases.length - 1 && (
                <div className="w-8 h-0.5 bg-gray-300" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Phase Cards */}
        <div className="space-y-4">
          {phases.map((phase) => (
            <PhaseCard
              key={phase.number}
              phase={phase}
              isExpanded={expandedPhase === phase.number || expandAll}
              onToggle={() => handleToggle(phase.number)}
            />
          ))}
        </div>

        {/* Footer Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 bg-white rounded-lg p-8 shadow-lg"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Your Journey Summarized
          </h3>
          <div className="space-y-2 text-gray-700">
            {phases.map((phase) => (
              <div key={phase.number} className="flex items-start gap-3">
                <span className={`font-bold ${phase.color} min-w-[80px]`}>
                  Phase {phase.number}:
                </span>
                <span>
                  <strong>{phase.title}</strong> - {phase.subtitle}
                  {phase.future && " (Future state)"}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-gray-700 font-medium mb-2">
              <strong>Each phase builds on the last.</strong> You can't skip ahead because the skills compound. But you can stop anywhere and already be dramatically more effective than where you started.
            </p>
            <p className="text-gray-600 italic">
              Tools like 8020agent are optional accelerators at any phase - showing what's possible, providing reference implementations, or handling complexity so you can focus on results instead of building from scratch.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
